import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { FaPlus, FaTimes } from 'react-icons/fa';
import {
  normalizeVersionFeatureList,
  versionFeatureListHaveChanges,
} from '../tenantHelpers';

const NEW_VERSION_KEY = '__new__';

const VersionFeaturesPanel = ({
  definitions,
  loading,
  error,
  updating,
  deleting,
  labelSuggestions,
  onSaveDefinition,
  onDeleteDefinition,
}) => {
  const [activeKey, setActiveKey] = useState(null);
  const [editorMode, setEditorMode] = useState('add');
  const [label, setLabel] = useState('');
  const [versionFeatures, setVersionFeatures] = useState([]);
  const [originalVersionFeatures, setOriginalVersionFeatures] = useState([]);
  const [newFeature, setNewFeature] = useState('');
  const [editorError, setEditorError] = useState('');

  const isBusy = loading || updating || deleting;

  const sortedDefinitions = useMemo(
    () => [...(definitions || [])].sort((a, b) => String(a.label).localeCompare(String(b.label))),
    [definitions]
  );

  const labelSuggestionOptions = useMemo(() => {
    const configured = new Set(
      (definitions || []).map((item) => String(item.label).trim().toLowerCase())
    );
    return (labelSuggestions || [])
      .filter((suggestion) => !configured.has(String(suggestion).trim().toLowerCase()))
      .slice(0, 12);
  }, [definitions, labelSuggestions]);

  useEffect(() => {
    if (activeKey === NEW_VERSION_KEY) return;
    if (activeKey && !sortedDefinitions.some((d) => d.label === activeKey)) {
      setActiveKey(null);
    }
  }, [sortedDefinitions, activeKey]);

  const resetEditorFields = () => {
    setLabel('');
    setVersionFeatures([]);
    setOriginalVersionFeatures([]);
    setNewFeature('');
    setEditorError('');
  };

  const startAdd = () => {
    setEditorMode('add');
    resetEditorFields();
    setActiveKey(NEW_VERSION_KEY);
  };

  const startEdit = (definition) => {
    const features = normalizeVersionFeatureList(definition.versionFeatures);
    setEditorMode('edit');
    setLabel(definition.label);
    setVersionFeatures(features);
    setOriginalVersionFeatures(features);
    setNewFeature('');
    setEditorError('');
    setActiveKey(definition.label);
  };

  const closeEditor = () => {
    setActiveKey(null);
    setEditorError('');
  };

  const addFeature = (value = newFeature) => {
    const feature = String(value || '').trim();
    if (!feature) return;
    if (versionFeatures.some((item) => item.toLowerCase() === feature.toLowerCase())) return;
    setVersionFeatures((prev) => [...prev, feature]);
    setNewFeature('');
  };

  const removeFeature = (feature) => {
    setVersionFeatures((prev) => prev.filter((item) => item !== feature));
  };

  const validateEditor = () => {
    const trimmedLabel = String(label || '').trim();
    if (editorMode === 'add' && !trimmedLabel) {
      setEditorError('Version label is required.');
      return null;
    }
    if (editorMode === 'add') {
      const duplicate = sortedDefinitions.some(
        (item) => String(item.label).trim().toLowerCase() === trimmedLabel.toLowerCase()
      );
      if (duplicate) {
        setEditorError('A version features definition already exists for this label.');
        return null;
      }
    }
    setEditorError('');
    return {
      label: trimmedLabel,
      versionFeatures: normalizeVersionFeatureList(versionFeatures),
    };
  };

  const handleSaveEditor = async () => {
    const entry = validateEditor();
    if (!entry) return;

    if (editorMode === 'edit' && !versionFeatureListHaveChanges(originalVersionFeatures, entry.versionFeatures)) {
      return;
    }

    const success = await onSaveDefinition(entry, editorMode === 'add');
    if (success) {
      if (editorMode === 'add') {
        setActiveKey(entry.label);
        setEditorMode('edit');
        setOriginalVersionFeatures(entry.versionFeatures);
      } else {
        setOriginalVersionFeatures(normalizeVersionFeatureList(entry.versionFeatures));
      }
    }
  };

  const handleDelete = async (definitionLabel) => {
    const trimmed = String(definitionLabel || '').trim();
    if (!trimmed) return;

    const confirm = await Swal.fire({
      title: 'Delete Version Features?',
      html: `
        Deleting this configuration removes the Version Features mapped to <strong>${trimmed}</strong>.
        <br /><br />
        This does <strong>not</strong> remove the ${trimmed} label from tenants.
      `,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete Version Features',
      cancelButtonText: 'Cancel',
    });

    if (!confirm.isConfirmed) return;

    const success = await onDeleteDefinition(trimmed);
    if (success && activeKey === trimmed) {
      setActiveKey(null);
      resetEditorFields();
    }
  };

  const editorHasChanges = editorMode === 'add'
    || versionFeatureListHaveChanges(originalVersionFeatures, versionFeatures);

  const showEditor = activeKey === NEW_VERSION_KEY || (activeKey && sortedDefinitions.some((d) => d.label === activeKey));

  const renderEditor = () => {
    if (!showEditor) {
      return (
        <div className="stm-feature-editor-panel stm-feature-editor-panel--empty">
          <p className="stm-feature-editor-panel__placeholder-title">Version features</p>
          <p className="stm-muted stm-features-section__helper">
            Global feature lists mapped to version labels on tenants.
          </p>
          <p className="stm-muted">Select a version on the left or add a new mapping.</p>
        </div>
      );
    }

    return (
      <div className="stm-feature-editor-panel">
        <div className="stm-feature-editor-panel__head">
          <h3 className="stm-features-section__title">
            {editorMode === 'add' ? 'New version mapping' : label}
          </h3>
          {editorMode === 'edit' && (
            <p className="stm-features-section__meta">Version features</p>
          )}
        </div>
        <div className="stm-version-def-editor stm-version-def-editor--compact">
          {editorMode === 'add' && (
            <>
              <p className="stm-modal__label stm-modal__label--compact">Version label</p>
              <input
                className="stm-version-def-label-input"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
                placeholder="e.g. V26.2DI"
                disabled={isBusy}
                list="stm-version-label-suggestions-panel"
              />
              <datalist id="stm-version-label-suggestions-panel">
                {labelSuggestionOptions.map((suggestion) => (
                  <option key={suggestion} value={suggestion} />
                ))}
              </datalist>
            </>
          )}

          <p className="stm-modal__label stm-modal__label--compact">Version features</p>
          <div className="stm-label-editor stm-label-editor--compact">
            {versionFeatures.length ? versionFeatures.map((feature) => (
              <button
                key={feature}
                type="button"
                className="stm-label-editor__chip"
                onClick={() => removeFeature(feature)}
                disabled={isBusy}
              >
                {feature}
                <FaTimes aria-hidden />
              </button>
            )) : (
              <span className="stm-muted">No features configured</span>
            )}
          </div>
          <div className="stm-label-editor__add">
            <input
              value={newFeature}
              onChange={(event) => setNewFeature(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  addFeature();
                }
              }}
              placeholder="Add version feature"
              disabled={isBusy}
            />
            <button type="button" className="stm-icon-btn" onClick={() => addFeature()} disabled={isBusy}>
              <FaPlus />
            </button>
          </div>
          {editorError && <p className="stm-field-error">{editorError}</p>}
        </div>
        <div className="stm-feature-editor-panel__actions stm-feature-editor-panel__actions--split">
          {editorMode === 'edit' && (
            <button
              type="button"
              className="stm-text-link stm-text-link--muted"
              onClick={() => handleDelete(label)}
              disabled={isBusy}
            >
              Delete
            </button>
          )}
          <div className="stm-feature-editor-panel__actions-right">
            <button type="button" className="stm-btn stm-btn--secondary stm-btn--sm" onClick={closeEditor} disabled={isBusy}>
              Cancel
            </button>
            <button
              type="button"
              className="stm-btn stm-btn--primary stm-btn--sm"
              disabled={isBusy || !editorHasChanges || (editorMode === 'add' && !String(label).trim())}
              onClick={handleSaveEditor}
            >
              {updating ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderList = () => (
    <div className="stm-version-list-panel">
      <div className="stm-version-list-panel__head">
        <h3 className="stm-features-section__title">Versions</h3>
        <button
          type="button"
          className="stm-btn stm-btn--secondary stm-btn--sm"
          onClick={startAdd}
          disabled={isBusy}
        >
          <FaPlus /> Add
        </button>
      </div>
      {loading ? (
        <p className="stm-muted stm-version-list-panel__empty">Loading...</p>
      ) : error ? (
        <p className="stm-field-error stm-version-list-panel__empty">{error}</p>
      ) : sortedDefinitions.length === 0 ? (
        <p className="stm-muted stm-version-list-panel__empty">No versions yet.</p>
      ) : (
        <div className="stm-version-list-panel__list">
          {sortedDefinitions.map((definition) => {
            const features = normalizeVersionFeatureList(definition.versionFeatures);
            const isActive = activeKey === definition.label;
            return (
              <button
                key={definition.label}
                type="button"
                className={`stm-version-pick-row ${isActive ? 'is-active' : ''}`}
                onClick={() => startEdit(definition)}
                disabled={isBusy}
              >
                <span className="stm-version-pick-row__label">{definition.label}</span>
                <span className="stm-version-pick-row__meta">
                  {features.length} feature{features.length === 1 ? '' : 's'}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="stm-features-workspace">
      <aside className="stm-features-workspace__aside">
        {renderList()}
      </aside>
      <div className="stm-features-workspace__main">
        {renderEditor()}
      </div>
    </div>
  );
};

export default VersionFeaturesPanel;
